const selectors = {
  skeletonID: "mobile-drawer-skeleton",
  drawerContent: ".mobile-drawer__content",
  drawerWrapper: ".mobile-drawer__wrapper",
  drawerClose: ".mobile-drawer__dragger",
  drawerDragger: ".mobile-drawer__dragger",
}

const modifiers = {
  isShown: "is-shown",
  isDragging: "is-dragging",
}

class MobileCustomDrawer {
  constructor() {
    this.drawerElement = null
    this.drawerSkeletonHTML = null
    this.drawerIsOpen = false
    this.templateId = null
    this.animationDelay = 300
    this.deltaGraggerPx = 30

    this.attachEventListeners()
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    this.handleCloseButtonClick = this.handleCloseButtonClick.bind(this)
  }

  init(event) {
    this.templateId = event.detail.templateId || null
    this.requestTemplate = event.detail.requestTemplate || null

    if ((!this.templateId || !this.requestTemplate) && this.drawerIsOpen) return


    this.loadSkeleton()

    this.requestTemplate ?
      this.loadTemplateFromRequest() :
      this.loadTemplateFromDOM()

    if (!this.templateHTML || !this.drawerSkeletonHTML) return

    this.renderDrawer()
    this.defineDrawerElements()
    this.openDrawer()
  }

  attachEventListeners() {
    window.addEventListener("mobileDrawerOpen",
      this.debounce_leading(this.init.bind(this), this.animationDelay))
  }

  loadSkeleton() {
    this.drawerSkeletonHTML = document.getElementById(
      selectors.skeletonID)?.content.cloneNode(true)
  }

  loadTemplateFromRequest() {
    const template = this.requestTemplate.querySelector(
      "#" + this.templateId)
    if (template) {
      this.templateHTML = template.content.cloneNode(true)
    }
  }

  loadTemplateFromDOM() {
    const template = document.getElementById(this.templateId)
    if (template) {
      this.templateHTML = template.content.cloneNode(true)
    }
  }

  renderDrawer() {
    if (this.drawerElement) return
    this.drawerElement = document.createElement("div")
    this.drawerElement.setAttribute("id", "mobile-drawer-" + this.templateId)
    this.drawerElement.classList.add("mobile-drawer")
    this.drawerElement.appendChild(this.drawerSkeletonHTML)
    this.drawerElement.querySelector(selectors.drawerContent)?.appendChild(this.templateHTML)
    document.body.appendChild(this.drawerElement)
  }

  openDrawer() {
    if (!this.drawerIsOpen) {
      setTimeout(() => {
        this.drawerIsOpen = true
        this.drawerElement.classList.add("open")
        this.addDrawerEvents()
      }, 10)
      this.lockScroll()
    }
  }

  defineDrawerElements() {
    this.drawerWrapper = this.drawerElement.querySelector(selectors.drawerWrapper)
    this.drawerClose = this.drawerElement.querySelector(selectors.drawerClose)
    this.drawerDragger = this.drawerElement.querySelector(selectors.drawerDragger)
  }

  addDrawerEvents() {

    document.addEventListener("click",
      this.handleOutsideClick)
    this.drawerClose?.addEventListener("click",
      this.handleCloseButtonClick)

    this.onMouseDown = this.onMouseDown.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)

    this.onTouchStart = this.onTouchStart.bind(this)
    this.onTouchMove = this.onTouchMove.bind(this)
    this.onTouchEnd = this.onTouchEnd.bind(this)


    if (this.drawerDragger) {
      this.drawerDragger.addEventListener("mousedown", this.onMouseDown)
      document.addEventListener("mousemove", this.onMouseMove)
      document.addEventListener("mouseup", this.onMouseUp)

      this.drawerDragger.addEventListener("touchstart", this.onTouchStart)
      document.addEventListener("touchmove", this.onTouchMove)
      document.addEventListener("touchend", this.onTouchEnd)
    }
  }

  removeDrawerEvents() {
    document.removeEventListener("click",
      this.handleOutsideClick)
    document.removeEventListener("mousemove", this.onMouseMove)
    document.removeEventListener("mouseup", this.onMouseUp)
    document.removeEventListener("touchmove", this.onTouchMove)
    document.removeEventListener("touchend", this.onTouchEnd)
  }

  closeDrawer() {
    this.unlockScroll()

    if (this.drawerIsOpen) {

      this.drawerElement.classList.remove("open")
      this.removeDrawerEvents()

      setTimeout(() => {
        this.drawerIsOpen = false
        this.removeDrawer()
      }, this.animationDelay)
    }
  }

  removeDrawer() {
    if (this.drawerElement) {
      document.body.removeChild(this.drawerElement)
      this.drawerElement = null
    }
  }

  handleOutsideClick(event) {
    if (this.drawerIsOpen && this.drawerElement && !this.drawerWrapper.contains(event.target)) {
      this.closeDrawer()
    }
  }

  handleCloseButtonClick() {
    this.closeDrawer()
  }

  onMouseDown(event) {
    this.startDrag(event, event.clientY)
  }

  onMouseMove(event) {
    this.drag(event, event.clientY)
  }

  onMouseUp(event) {
    this.endDrag(event, event.clientY)
  }

  onTouchStart(event) {
    this.startDrag(event, event.touches[0].clientY)
  }

  onTouchMove(event) {
    this.drag(event, event.touches[0].clientY)
  }

  onTouchEnd(event) {
    this.endDrag(event, event.changedTouches[0].clientY)
  }

  startDrag(event, clientY) {
    this.isDragging = true
    this.startY = clientY
    const style = window.getComputedStyle(this.drawerElement)
    const matrix = new DOMMatrixReadOnly(style.transform)
    this.initialTranslateY = matrix.m42

    this.drawerElement.classList.add(modifiers.isDragging)
  }

  drag(event, clientY) {
    if (!this.isDragging) {
      return null
    }

    const deltaY = clientY - this.startY

    const newTranslateY = Math.max(this.initialTranslateY + deltaY, 0)
    this.drawerWrapper.style.transform = `translateY(${newTranslateY}px)`
  }

  endDrag(event, clientY) {
    if (!this.isDragging) {
      return null
    }

    const deltaY = clientY - this.startY

    this.drawerElement.classList.remove(modifiers.isDragging)
    this.drawerWrapper.style.transform = ""

    if (deltaY > 30) {
      this.closeDrawer()
    }

    this.isDragging = false
  }

  lockScroll() {
    document.querySelector("body").style.overflow = "scroll"
    document.querySelector("html").style.overflow = "hidden"
    document.querySelector("html").style.maxHeight = "100vh"
  }

  unlockScroll() {
    document.querySelector("body").style.overflow = ""
    document.querySelector("html").style.overflow = ""
    document.querySelector("html").style.maxHeight = ""
  }


  debounce(fn, wait) {
    let t
    return (...args) => {
      clearTimeout(t)
      t = setTimeout(() => fn.apply(this, args), wait)
    }
  }

  debounce_leading(func, timeout = 300) {
    let timer
    return (...args) => {
      if (!timer) {
        func.apply(this, args)
      }
      clearTimeout(timer)
      timer = setTimeout(() => {
        timer = undefined
      }, timeout)
    }
  }
}

export default MobileCustomDrawer
